import type { Level } from "./types";

/**
 * The five labyrinths, ramping from a 6×6 with one wall to an 8×8 with three.
 * Each comes from the kept design tool (`scripts/tune-levels.ts`, a seeded
 * search whose every candidate optimum is proven by a real HiGHS solve of the
 * interdiction MILP, with the budget held strictly below the maze's minimum
 * vertex cut so the hero can never be sealed off).
 *
 * `base` is the open shortest path; `opt` is the **proven** longest path the
 * budget can force; `greedy` is the myopic one-wall-at-a-time baseline.
 * Labyrinth 1 is friendly — a single wall already finds the optimum
 * (greedy = opt). Labyrinths 2–5 are designed so the myopic greedy provably
 * falls short (11<13, 12<14, 7<13, 6<14): the long detours need walls that
 * work together. `twoStar` is the 2-star length threshold; matching `opt`
 * earns the third star. All five optima are re-proven by real HiGHS (and the
 * smallest by brute force) in `levels.test.ts`, and live in the browser at
 * reveal time.
 *
 * Seeds (in the tuner): 92 / 985 / 1055 / 1132 / 1221.
 */
export const LEVELS: Level[] = [
  {
    name: "The Antechamber",
    rows: 6,
    cols: 6,
    baseWalls: [[0, 3], [1, 1], [2, 2], [2, 3], [2, 4], [4, 0], [4, 1]],
    start: [2, 1],
    goal: [3, 4],
    budget: 1,
    base: 4,
    greedy: 12,
    opt: 12,
    twoStar: 9,
  },
  {
    name: "The Twisting Vaults",
    rows: 6,
    cols: 7,
    baseWalls: [[0, 6], [1, 6], [2, 4], [2, 6], [3, 1], [3, 2], [4, 2], [4, 3], [4, 5], [5, 0]],
    start: [2, 1],
    goal: [3, 5],
    budget: 2,
    base: 5,
    greedy: 11,
    opt: 13,
    twoStar: 10,
  },
  {
    name: "The Sunken Crypt",
    rows: 7,
    cols: 7,
    baseWalls: [[0, 3], [1, 3], [1, 4], [1, 5], [2, 1], [2, 6], [4, 2], [4, 4], [4, 5], [5, 1], [5, 2], [6, 6]],
    start: [3, 1],
    goal: [3, 5],
    budget: 2,
    base: 4,
    greedy: 12,
    opt: 14,
    twoStar: 10,
  },
  {
    name: "The Hall of Echoes",
    rows: 7,
    cols: 8,
    baseWalls: [[0, 0], [1, 2], [4, 0], [4, 4]],
    start: [3, 1],
    goal: [3, 6],
    budget: 3,
    base: 5,
    greedy: 7,
    opt: 13,
    twoStar: 10,
  },
  {
    name: "The Treasury Maze",
    rows: 8,
    cols: 8,
    baseWalls: [[0, 1], [1, 5], [4, 7]],
    start: [4, 1],
    goal: [3, 6],
    budget: 3,
    base: 6,
    greedy: 6,
    opt: 14,
    twoStar: 11,
  },
];
